import _ from 'lodash'

// NetSuite custentity_cbc_contact_login_type role names (custom-token-payload.js), normalised to snake_case
const USER_ROLE_MAP = {
  user: 'user',
  supervisor: 'supervisor',
  areaManager: 'area_manager',
  manager: 'manager',
  kingsCourt: 'kings_court',
  viewOnly: 'view_only',
  superAdmin: 'super_admin'
}

// custrecord_cbc_contact_budget_type (BUDGET_TYPES in helpers/constant.js): 1=Budget, 2=Points, 3=Allocation
const BUDGET_TYPE_MAP = {
  1: 'budget',
  2: 'points',
  3: 'allocation'
}

function getUserRole(accountInfo) {
  const rawRole = _.get(accountInfo, 'permissions[0]')
  return USER_ROLE_MAP[rawRole] || 'unknown'
}

// isEvolveUser/isEnhancedUser/isSuperUser aren't a single field - this combines them into a ladder
function getAdminTier(accountInfo) {
  if (accountInfo.isSuperUser) return 'super'
  if (accountInfo.isEnhancedUser) return 'enhanced'
  if (accountInfo.isEvolveUser) return 'low'
  return 'none'
}

function getBudgetType(accountInfo) {
  return BUDGET_TYPE_MAP[Number(accountInfo.budgetType)] || 'unknown'
}

// hasPermissionFullCatalogue/hasPermissionMyCatalogue/hasPermissionUserCatalogue/hasPermissionManpack
// aren't mutually exclusive - some NetSuite catalogue-view codes grant several at once (confirmed live:
// uniform@stormaviation.com has all four). Returns the list of every catalogue the account actually
// has - GA4 and Clarity consume this differently (see tagSessionAttributes): GA4 user property values
// are capped at 36 chars, so a joined "full+my_catalogue+user_catalogue+wearerpack" string (43 chars)
// silently truncates for exactly this account. Clarity's set() natively takes string|string[] per key
// and exact-matches against each value, so it gets the list as-is.
function getCatalogueAccessList(store) {
  return [
    store.getters['account/hasPermissionFullCatalogue'] && 'full',
    store.getters['account/hasPermissionMyCatalogue'] && 'my_catalogue',
    store.getters['account/hasPermissionUserCatalogue'] && 'user_catalogue',
    store.getters['account/hasPermissionManpack'] && 'wearerpack'
  ].filter(Boolean)
}

// Microsoft Clarity's loader (index.html) defines window.clarity as a stub
// (`function(){(c[a].q=c[a].q||[]).push(arguments)}`) that queues calls until the real
// tracking script (loaded async, separately from the lightweight loader script) replaces
// it. In production this real script can take ~3s to finish loading (measured on
// my-smi.com: loader script finishes ~2.4s in, the actual clarity.js implementation not
// until ~3.0s), which is longer than most of our tagging calls take to fire - especially
// the returning-session path, which tags as soon as /api/session resolves, often under
// 2s. Relying on the stub's queue draining correctly for that whole window on every
// session isn't reliable enough for something GA4 doesn't need (GTM's dataLayer queues by
// design regardless of load timing) - so Clarity calls specifically wait for real
// readiness instead of firing blind the instant the stub exists. Doesn't block the
// caller: resolves in the background, GA4/dataLayer tagging is unaffected and already
// fired by the time this even starts.
//
// Detects readiness by checking the function's own source for the stub's tell-tale
// "c[a].q" fragment (confirmed via testing - flips the instant the real script replaces
// window.clarity), not by checking window.clarity.q's presence: .q is only created
// lazily on the stub's *first* call, so if nothing has called clarity() yet, .q wouldn't
// exist even though it's still the stub - that would look "ready" when it isn't.
function isClarityStub() {
  return typeof window.clarity === 'function' && window.clarity.toString().indexOf('c[a].q') !== -1
}

function waitForClarityReady(maxWaitMs = 8000, pollIntervalMs = 100) {
  return new Promise(resolve => {
    if (!isClarityStub()) {
      resolve()
      return
    }
    const start = Date.now()
    const intervalId = setInterval(() => {
      if (!isClarityStub() || Date.now() - start >= maxWaitMs) {
        clearInterval(intervalId)
        resolve()
      }
    }, pollIntervalMs)
  })
}

function tagClarity(attributes, catalogueAccessList) {
  if (typeof window.clarity !== 'function') {
    return
  }
  waitForClarityReady().then(() => {
    Object.entries(attributes).forEach(([key, value]) => {
      window.clarity('set', key, value)
    })
    // Clarity's set() takes string|string[] for one key and exact-matches each value independently,
    // so "everyone with wearerpack access" is filterable directly - no 36-char limit here to work around.
    window.clarity('set', 'catalogue_access', catalogueAccessList.length ? catalogueAccessList : ['none'])
  })
}

/**
 * Sets the 7 session-level attributes as GA4 user properties (via a dataLayer push
 * for GTM to pick up) and Microsoft Clarity custom tags. Call once, as early as possible
 * after session data is populated in the store and before the first pageview - see
 * ticket doc for the specific insertion points.
 *
 * Cancels the index.html fallback timer (see the inline script before the GTM snippet)
 * so `session_attributes_timeout` doesn't also fire once this completes. Still pushes
 * `session_attributes` even if the timer already fired first (e.g. a slow /api/session) -
 * Clarity doesn't need the strict before-first-pageview timing, and later GA4 events in
 * the same session still benefit from the user properties even if the very first pageview
 * missed them.
 */
export async function tagSessionAttributes(store) {
  const accountInfo = store.getters['account/getAccountInfo']
  const setting = store.getters['account/setting']

  // customer_id can legitimately be 0 for evolve/admin accounts not tied to a specific
  // customer - only null/undefined means "not logged in yet", a plain falsy check would
  // wrongly skip tagging for those accounts
  if (_.isNil(_.get(accountInfo, 'customer_id'))) {
    return
  }

  if (window.__sessionAttrTimeoutId) {
    clearTimeout(window.__sessionAttrTimeoutId)
    window.__sessionAttrTimeoutId = null
  }

  const catalogueAccessList = getCatalogueAccessList(store)

  // GA4 user properties: one boolean per catalogue instead of a joined string, since GA4 caps
  // property values at 36 chars and the joined form breaks for exactly the accounts this exists to
  // cover (see getCatalogueAccessList's comment).
  const attributes = {
    user_role: getUserRole(accountInfo),
    admin_tier: getAdminTier(accountInfo),
    budget_type: getBudgetType(accountInfo),
    catalogue_full: catalogueAccessList.includes('full') ? 'true' : 'false',
    catalogue_my: catalogueAccessList.includes('my_catalogue') ? 'true' : 'false',
    catalogue_user: catalogueAccessList.includes('user_catalogue') ? 'true' : 'false',
    catalogue_wearerpack: catalogueAccessList.includes('wearerpack') ? 'true' : 'false',
    is_approver: setting?.isApprover ? 'true' : 'false',
    is_punchout: setting?.punchoutSession ? 'true' : 'false',
    customer_company_id: accountInfo.customer_company_hash
  }

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ event: 'session_attributes', tagging_status: 'complete', ...attributes })

  tagClarity(attributes, catalogueAccessList)
}
